import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ListPlus, AlertCircle, Loader2 } from 'lucide-react';
import { useEditorStore } from '@/stores/editor/editor-store';
import { useViewerStore } from '@/stores/viewer/viewer-store';
import { useToast } from '@/hooks/use-toast';
import type { KanbanFieldMapping } from '@/types/pm-types';
import type { Component } from '@/types';

interface AddKanbanItemModalProps {
  isOpen: boolean;
  onClose: () => void;
  kanbanComponentId: string;
}

interface ColumnDef {
  id: string;
  label?: string;
  title?: string;
  color?: string;
}

const DEFAULT_FIELD_MAPPING: KanbanFieldMapping = {
  title: 'title',
  column: 'status',
  id: 'id',
  priority: 'priority',
  assignee: 'assignee',
  sortOrder: 'sortOrder',
};

const DEFAULT_PRIORITIES = ['low', 'medium', 'high', 'critical'];

export function AddKanbanItemModal({ isOpen, onClose, kanbanComponentId }: AddKanbanItemModalProps) {
  const editorStore = useEditorStore();
  const viewerStore = useViewerStore();
  const { toast } = useToast();

  const [fieldMapping, setFieldMapping] = useState<KanbanFieldMapping>(DEFAULT_FIELD_MAPPING);
  const [columns, setColumns] = useState<ColumnDef[]>([]);
  const [existingPriorities, setExistingPriorities] = useState<string[]>(DEFAULT_PRIORITIES);

  // Form state
  const [title, setTitle] = useState('');
  const [column, setColumn] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('');
  const [assignee, setAssignee] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [tags, setTags] = useState('');

  const [titleError, setTitleError] = useState<string | null>(null);
  const [columnError, setColumnError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Extract field mapping, columns, and existing data when modal opens
  useEffect(() => {
    if (!isOpen || !kanbanComponentId) return;

    let component: Component | undefined | null =
      editorStore.getComponent?.(kanbanComponentId);
    if (!component) {
      component = viewerStore.getComponentById?.(kanbanComponentId);
    }
    if (!component) return;

    const props = component.properties ?? {};
    const fm = (props.fieldMapping as KanbanFieldMapping) ?? DEFAULT_FIELD_MAPPING;
    setFieldMapping(fm);

    // Extract columns
    const cols: ColumnDef[] = Array.isArray(props.columns)
      ? (props.columns as ColumnDef[])
      : [{ id: 'todo', label: 'To Do' }, { id: 'in-progress', label: 'In Progress' }, { id: 'done', label: 'Done' }];
    setColumns(cols);

    // Extract existing data for priority suggestions
    const data: Record<string, unknown>[] =
      (Array.isArray(props.sampleData) ? props.sampleData : null) ??
      (Array.isArray(props.data) ? props.data : null) ??
      [];

    if (fm.priority) {
      const priorities = [...new Set(
        data
          .map((row) => String(row[fm.priority!] ?? ''))
          .filter(Boolean)
      )];
      // Merge with defaults, preserving order
      const merged = [...new Set([...priorities, ...DEFAULT_PRIORITIES])];
      setExistingPriorities(merged);
    }

    // Reset form
    setTitle('');
    setColumn(cols[0]?.id ?? '');
    setDescription('');
    setPriority('');
    setAssignee('');
    setDueDate('');
    setTags('');
    setTitleError(null);
    setColumnError(null);
    setSubmitError(null);
    setIsSubmitting(false);
  }, [isOpen, kanbanComponentId, editorStore, viewerStore]);

  const handleSubmit = () => {
    const trimmedTitle = title.trim();
    if (!trimmedTitle) {
      setTitleError('Title is required');
      return;
    }
    if (!column) {
      setColumnError('Column is required');
      return;
    }

    setTitleError(null);
    setColumnError(null);
    setSubmitError(null);
    setIsSubmitting(true);

    // Build record using field mapping
    const record: Record<string, unknown> = {};
    record[fieldMapping.id || 'id'] = crypto.randomUUID().slice(0, 8);
    record[fieldMapping.title] = trimmedTitle;
    record[fieldMapping.column] = column;

    if (fieldMapping.description && description.trim()) {
      record[fieldMapping.description] = description.trim();
    }
    if (fieldMapping.priority && priority) {
      record[fieldMapping.priority] = priority;
    }
    if (fieldMapping.assignee && assignee.trim()) {
      record[fieldMapping.assignee] = assignee.trim();
    }
    if (fieldMapping.dueDate && dueDate) {
      record[fieldMapping.dueDate] = dueDate;
    }
    if (fieldMapping.tags && tags.trim()) {
      record[fieldMapping.tags] = tags.split(',').map(t => t.trim()).filter(Boolean);
    }
    if (fieldMapping.sortOrder) {
      record[fieldMapping.sortOrder] = Date.now(); // auto-sort to end
    }

    const success = addItemToKanbanData(kanbanComponentId, record);
    setIsSubmitting(false);

    if (success) {
      onClose();
      setTimeout(() => {
        toast({ title: 'Success', description: 'Item added successfully' });
      }, 100);
    } else {
      setSubmitError('Failed to add item to kanban board');
    }
  };

  const handleTitleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isSubmitting) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const getColumnLabel = (col: ColumnDef) => col.label || col.title || col.id;

  const inputClass = 'h-10 bg-white border-gray-200 text-gray-900 placeholder:text-gray-400 focus:border-primary focus:ring-primary';
  const labelClass = 'text-sm font-medium text-gray-700';

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent variant="light" className="sm:max-w-lg p-0">
        {/* Header */}
        <div className="px-6 pt-6 pb-4">
          <div className="flex items-start gap-4">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10">
              <ListPlus className="h-5 w-5 text-primary" />
            </div>
            <div className="space-y-1">
              <h3 className="text-lg font-semibold text-gray-900">
                Add New Item
              </h3>
              <p className="text-sm text-gray-500">
                Add an item to the kanban board. Fields marked with * are required.
              </p>
            </div>
          </div>
        </div>

        {/* Separator */}
        <div className="border-t border-gray-100" />

        {/* Body */}
        <div className="px-6 py-5 space-y-5 max-h-[60vh] overflow-y-auto">
          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="kanban-item-title" className={labelClass}>
              Title <span className="text-red-500">*</span>
            </Label>
            <Input
              id="kanban-item-title"
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                if (titleError) setTitleError(null);
              }}
              onKeyDown={handleTitleKeyDown}
              placeholder="e.g. Design homepage layout"
              className={`${inputClass} ${titleError ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
              autoFocus
              disabled={isSubmitting}
            />
            {titleError && (
              <div className="flex items-center gap-1.5 text-red-600">
                <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                <p className="text-xs">{titleError}</p>
              </div>
            )}
          </div>

          {/* Column + Priority row */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="kanban-item-column" className={labelClass}>
                Column <span className="text-red-500">*</span>
              </Label>
              <Select
                value={column}
                onValueChange={(val) => {
                  setColumn(val);
                  if (columnError) setColumnError(null);
                }}
                disabled={isSubmitting}
              >
                <SelectTrigger
                  id="kanban-item-column"
                  className={`${inputClass} ${columnError ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
                >
                  <SelectValue placeholder="Select column" />
                </SelectTrigger>
                <SelectContent>
                  {columns.map((col) => (
                    <SelectItem key={col.id} value={col.id}>{getColumnLabel(col)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {columnError && (
                <div className="flex items-center gap-1.5 text-red-600">
                  <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                  <p className="text-xs">{columnError}</p>
                </div>
              )}
            </div>

            {fieldMapping.priority && (
              <div className="space-y-2">
                <Label htmlFor="kanban-item-priority" className={labelClass}>
                  Priority
                </Label>
                <Select
                  value={priority}
                  onValueChange={setPriority}
                  disabled={isSubmitting}
                >
                  <SelectTrigger id="kanban-item-priority" className={inputClass}>
                    <SelectValue placeholder="Select priority" />
                  </SelectTrigger>
                  <SelectContent>
                    {existingPriorities.map((p) => (
                      <SelectItem key={p} value={p}>
                        {p.charAt(0).toUpperCase() + p.slice(1)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {/* Assignee + Due Date row */}
          {(fieldMapping.assignee || fieldMapping.dueDate) && (
            <div className="grid grid-cols-2 gap-4">
              {fieldMapping.assignee && (
                <div className="space-y-2">
                  <Label htmlFor="kanban-item-assignee" className={labelClass}>
                    Assignee
                  </Label>
                  <Input
                    id="kanban-item-assignee"
                    value={assignee}
                    onChange={(e) => setAssignee(e.target.value)}
                    placeholder="e.g. Alice"
                    className={inputClass}
                    disabled={isSubmitting}
                  />
                </div>
              )}

              {fieldMapping.dueDate && (
                <div className="space-y-2">
                  <Label htmlFor="kanban-item-due-date" className={labelClass}>
                    Due Date
                  </Label>
                  <Input
                    id="kanban-item-due-date"
                    type="date"
                    value={dueDate}
                    onChange={(e) => setDueDate(e.target.value)}
                    className={inputClass}
                    disabled={isSubmitting}
                  />
                </div>
              )}
            </div>
          )}

          {/* Description */}
          {fieldMapping.description && (
            <div className="space-y-2">
              <Label htmlFor="kanban-item-description" className={labelClass}>
                Description
              </Label>
              <Textarea
                id="kanban-item-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Add a description..."
                className="bg-white border-gray-200 text-gray-900 placeholder:text-gray-400 focus:border-primary focus:ring-primary min-h-[80px]"
                disabled={isSubmitting}
              />
            </div>
          )}

          {/* Tags */}
          {fieldMapping.tags && (
            <div className="space-y-2">
              <Label htmlFor="kanban-item-tags" className={labelClass}>
                Tags <span className="text-gray-400 font-normal">(comma-separated)</span>
              </Label>
              <Input
                id="kanban-item-tags"
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                placeholder="e.g. design, frontend, urgent"
                className={inputClass}
                disabled={isSubmitting}
              />
            </div>
          )}

          {/* Submit Error Banner */}
          {submitError && (
            <div className="flex items-start gap-2 rounded-lg bg-red-50 border border-red-200 p-3">
              <AlertCircle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
              <p className="text-sm text-red-700">{submitError}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50/50">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isSubmitting}
            className="sm:w-auto w-full border-gray-200 text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting || !title.trim() || !column}
            className="sm:w-auto w-full min-w-[120px]"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Adding...
              </>
            ) : (
              'Add Item'
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Adds a new item record to the Kanban board's data in the store.
 */
function addItemToKanbanData(
  componentId: string,
  newRecord: Record<string, unknown>,
): boolean {
  try {
    const editorState = useEditorStore.getState();
    let component: Component | undefined | null = editorState.getComponent?.(componentId);
    let updateComponent = editorState.updateComponent;

    if (!component) {
      const viewerState = useViewerStore.getState();
      component = viewerState.getComponentById?.(componentId);
      updateComponent = viewerState.updateComponent;
    }

    if (!component) {
      console.error(`[AddKanbanItem] Component "${componentId}" not found`);
      return false;
    }

    const props = component.properties ?? {};

    const currentData: Record<string, unknown>[] =
      (Array.isArray(props.sampleData) ? (props.sampleData as Record<string, unknown>[]) : null) ??
      (Array.isArray(props.data) ? (props.data as Record<string, unknown>[]) : null) ??
      [];

    updateComponent(componentId, {
      properties: {
        ...props,
        sampleData: [...currentData, newRecord],
      },
    });

    return true;
  } catch (error) {
    console.error('[AddKanbanItem] Error adding item:', error);
    return false;
  }
}
